import type { SchemaOrgNode } from '@core-mkt/services/schema/schema-org-node.model';
import { getOrganizationId } from '@core-mkt/services/schema/organization.schema';

/**
 * Shape of a single hardcoded testimonial in `review-data.ts`.
 * Optional fields vary by variant — column has `description`, carousel has `date`,
 * card / column-type2 have `image`. The schema function only emits properties that
 * are present.
 */
export interface ReviewItem {
  name: string;
  review: string;
  header?: string;
  description?: string;
  date?: string;
  image?: { src?: string; altText?: string };
}

export interface ReviewListSchemaData {
  /** Stable identifier for this list — typically `${pageUrl}#reviews-${variant}`. */
  listId: string;
  /** Human-readable name for the ItemList — appears in JSON-LD. */
  listName: string;
  /** Site root for the Organization cross-reference. */
  siteUrl: string;
  /** Brand name used on the cross-referenced Organization. */
  organizationName: string;
  /** The hardcoded review items for this variant. */
  items: ReviewItem[];
}

/**
 * Builds an ItemList of Review references plus the Review nodes themselves.
 * Each Review cross-references the Organization via `itemReviewed`. Returns null
 * for empty / undefined item arrays so the caller can register safely.
 *
 * Review `@id`s are scoped to the variant's `listId` (not just the page URL) to
 * prevent collisions when multiple variants render on the same page — each
 * variant has different testimonial content even though the indexes overlap.
 * CMS-authored fields (review body, header, list name) are HTML-stripped.
 */
export function buildReviewListSchema(data: ReviewListSchemaData | null): SchemaOrgNode[] | null {
  if (!data || !Array.isArray(data.items) || data.items.length === 0) return null;

  const orgRef = {
    '@id': getOrganizationId(data.siteUrl),
    '@type': 'EducationalOrganization' as const,
    name: data.organizationName,
  };

  const reviewNodes: SchemaOrgNode[] = data.items.map((item, index) => {
    const reviewId = `${data.listId}-review-${index + 1}`;
    const node: SchemaOrgNode = {
      '@type': 'Review',
      '@id': reviewId,
      author: { '@type': 'Person', name: item.name },
      reviewBody: stripHtml(item.review),
      itemReviewed: orgRef,
    };
    if (item.header) {
      const headerText = stripHtml(item.header);
      if (headerText) node['name'] = headerText;
    }
    if (item.date && isValidDate(item.date)) node['datePublished'] = isoDate(item.date);
    return node;
  });

  // SECURITY: never fall back to the raw listName — if stripHtml returns empty
  // (e.g. CMS title was `<a></a>`), we'd otherwise emit unsanitized HTML into
  // the JSON-LD `name` field. Drop the field instead.
  const cleanedName = stripHtml(data.listName);
  const itemList: SchemaOrgNode = {
    '@type': 'ItemList',
    '@id': data.listId,
    numberOfItems: reviewNodes.length,
    itemListElement: reviewNodes.map((review, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: { '@id': review['@id'] },
    })),
  };
  if (cleanedName) itemList['name'] = cleanedName;

  return [...reviewNodes, itemList];
}

/**
 * Strip HTML tags from CMS-authored review text. CMS prose can include inline
 * markup (e.g. `<span class="...">...</span>` for highlighting, `<h2>` wrappers
 * on titles); leaving those in JSON-LD is wrong both as Schema.org content and
 * as a search-snippet display. Like `tabbed-content.schema.ts` it strips tags
 * and collapses whitespace, but it intentionally diverges on anchors: reviews
 * drop anchors *and their inner text* (link text like "click here" reads as
 * spam in a review snippet), whereas `tabbed-content` keeps anchor inner text.
 *
 * Defense-in-depth: input is bounded to 10KB to cap regex work on pathological
 * CMS payloads, HTML comments are stripped (the `<[^>]*>` rule does not match
 * across comment closers), and any leftover lone `<` / `>` are removed so they
 * cannot survive into JSON-LD even if a tag was malformed.
 */
const STRIP_HTML_MAX_LENGTH = 10_000;
function stripHtml(input: string | undefined): string {
  if (!input) return '';
  const bounded = input.length > STRIP_HTML_MAX_LENGTH ? input.slice(0, STRIP_HTML_MAX_LENGTH) : input;
  return bounded
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<a\b[^>]*>[\s\S]*?<\/a>/gi, '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/[<>]/g, '')
    .replace(/\s+/g, ' ')
    .replace(/ ([.,!?;:])/g, '$1')
    .trim();
}

function isValidDate(input: string): boolean {
  const parsed = Date.parse(input);
  return !Number.isNaN(parsed);
}

function isoDate(input: string): string {
  // Use toISOString().slice(0, 10) so we get YYYY-MM-DD without timezone noise.
  return new Date(input).toISOString().slice(0, 10);
}
